import { union, without } from "lodash";

import { dynamicRequestCancellationEndpointsKey, requestCancellationIgnoredEndpointsKey } from "../../constants";

/**
 * -----*  Ignored Endpoints
 */

/**
 * Retrieves the list of ignored endpoints for request cancellation from sessionStorage.
 *
 * @returns An array of endpoint strings currently stored as ignored; returns an empty array if not set or if parsing fails.
 */
export function getIgnoredEndpoints(): string[] {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[getIgnoredEndpoints] sessionStorage is not available.');
    return [];
  }

  const value = sessionStorage.getItem(requestCancellationIgnoredEndpointsKey);
  if (!value) return [];

  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [];
}

/**
 * Updates the sessionStorage entry for ignored endpoints used in request cancellation logic.
 *
 * @param endpoints - An array of endpoint strings to be added to the ignored endpoints list.
 */
export function updateIgnoredEndpoints(endpoints: string[]): void {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[updateIgnoredEndpoints] sessionStorage is not available. Ignored endpoints cannot be persisted.');
    return
  }

  const key = requestCancellationIgnoredEndpointsKey;
  const stored = JSON.parse(sessionStorage.getItem(key) ?? '[]');
  const updated = union(stored, endpoints);

  sessionStorage.setItem(key, JSON.stringify(updated));
}


/**
 * Removes one or more endpoints from the ignored endpoints list in sessionStorage.
 *
 * @param endpoints - An array of endpoint strings to remove from the ignored list.
 */
export function removeIgnoredEndpoints(endpoints: string[]): void {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[removeIgnoredEndpoints] sessionStorage is not available. Ignored endpoints cannot be updated.');
    return;
  }

  const key = requestCancellationIgnoredEndpointsKey;
  const stored: string[] = JSON.parse(sessionStorage.getItem(key) ?? '[]');
  const updated = without(stored, ...endpoints);

  sessionStorage.setItem(key, JSON.stringify(updated));
}

/**
 * -----*  Dynamic Request Endpoints
 */

/**
 * Retrieves the list of dynamic request endpoints for request cancellation from sessionStorage.
 *
 * @returns An array of endpoint strings currently stored as dynamic request; returns an empty array if not set or if parsing fails.
 */
export function getDynamicReqEndpoints(): string[] {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[getDynamicReqEndpoints] sessionStorage is not available.');
    return [];
  }

  const value = sessionStorage.getItem(dynamicRequestCancellationEndpointsKey);
  if (!value) return [];

  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [];
}

/**
 * Updates the sessionStorage entry for dynamic request endpoints used in request cancellation logic.
 *
 * @param endpoints - An array of endpoint strings to be added to the dynamic request endpoints list.
 */
export function updateDynamicReqEndpoints(endpoints: string[]): void {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[updateDynamicReqEndpoints] sessionStorage is not available. Dynamic request endpoints cannot be persisted.');
    return
  }

  const key = dynamicRequestCancellationEndpointsKey;
  const stored = JSON.parse(sessionStorage.getItem(key) ?? '[]');
  const updated = union(stored, endpoints);

  sessionStorage.setItem(key, JSON.stringify(updated));
}

/**
 * Removes one or more endpoints from the dynamic request endpoints list in sessionStorage.
 *
 * @param endpoints - An array of endpoint strings to remove from the dynamic request list.
 */
export function removeDynamicReqEndpoints(endpoints: string[]): void {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[removeDynamicReqEndpoints] sessionStorage is not available. Dynamic request endpoints cannot be updated.');
    return;
  }

  const key = dynamicRequestCancellationEndpointsKey;
  const stored: string[] = JSON.parse(sessionStorage.getItem(key) ?? '[]');
  const updated = without(stored, ...endpoints);

  sessionStorage.setItem(key, JSON.stringify(updated));
}

/**
 * Clears all dynamically ignored endpoints for request cancellation from sessionStorage.
 * Removes the entry associated with dynamicRequestCancellationEndpointsKey from sessionStorage.
 */
export function clearDynamicReqEndpoints(): void {

  if (typeof sessionStorage === 'undefined' || !sessionStorage) {
    console.warn('[getDynamicReqEndpoints] sessionStorage is not available.');
    return;
  }

  sessionStorage.removeItem(dynamicRequestCancellationEndpointsKey)
}
